// src/pages/ChatZustimmungPage.tsx
//
// Zustimmungsseite des Chat-Connectors XTAX DATEV (XTAX GmbH & Co. KG,
// 03.11.2026, Freigabe Tim), öffentlich unter /oauth/consent.
//
// Verbindet ein Mitarbeiter in Claude den Connector "XTAX DATEV", leitet
// Supabase Auth (OAuth-2.1-Server) ihn mit ?authorization_id=... hierher.
// Die Seite
//   1. meldet ihn mit Google an seinem persönlichen Chat-Konto an,
//   2. prüft, dass es ein Chat-Konto ist (app_metadata.rolle = chat),
//   3. zeigt, welche Anwendung Zugriff möchte, und
//   4. gibt seine Zustimmung oder Ablehnung an Supabase weiter und leitet
//      zurück zu Claude.
//
// GETRENNTE SITZUNG: Die Anmeldung läuft über eine eigene Verbindung mit
// eigenem Speicherplatz im Browser (SPEICHER_SCHLUESSEL). Die
// Intranet-Sitzung des gemeinsamen Kontos (src/shared/services/supabase.ts)
// bleibt davon unberührt. Die Verbindung entsteht erst beim Öffnen dieser
// Seite, nicht beim Laden des Intranets.
//
// Die Supabase-Bibliothek des Intranets kennt die Zustimmungsfunktionen noch
// nicht; die Seite ruft die beiden Endpunkte des Anmeldedienstes direkt auf
// (GET und POST /auth/v1/oauth/authorizations/<id>, Aufbau wie in
// @supabase/auth-js ab 2.8x).

import React, { useCallback, useEffect, useState } from 'react';
import { createClient, Session, SupabaseClient } from '@supabase/supabase-js';

const SPEICHER_SCHLUESSEL = 'xtax-chat-connector-zustimmung';
const CHAT_ROLLE = 'chat';
const API_VERSION = '2024-01-01';

const supabaseUrl = (process.env.REACT_APP_SUPABASE_URL || '').replace(/\/$/, '');
const anonSchluessel = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

let verbindung: SupabaseClient | null = null;

/** Eigene Verbindung der Zustimmungsseite, erst beim ersten Aufruf angelegt. */
function zustimmungsVerbindung(): SupabaseClient {
  if (!verbindung) {
    verbindung = createClient(supabaseUrl, anonSchluessel, {
      auth: {
        storageKey: SPEICHER_SCHLUESSEL,
        flowType: 'pkce',
        detectSessionInUrl: true,
        persistSession: true,
        autoRefreshToken: false
      }
    });
  }
  return verbindung;
}

interface Anfrage {
  authorization_id: string;
  client?: { name?: string; uri?: string };
  scope?: string;
}

type Zustand =
  | { art: 'laden' }
  | { art: 'anmelden' }
  | { art: 'kein_chat_konto'; email: string }
  | { art: 'zustimmen'; anfrage: Anfrage; email: string; session: Session }
  | { art: 'weiterleiten' }
  | { art: 'fehler'; text: string };

async function anmeldedienst(pfad: string, session: Session, init: RequestInit = {}): Promise<Record<string, unknown>> {
  const antwort = await fetch(`${supabaseUrl}/auth/v1${pfad}`, {
    ...init,
    headers: {
      apikey: anonSchluessel,
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
      'X-Supabase-Api-Version': API_VERSION
    }
  });
  const daten = await antwort.json().catch(() => ({}));
  if (!antwort.ok) {
    const meldung = (daten && (daten.msg || daten.message || daten.error_description || daten.error)) || `HTTP ${antwort.status}`;
    throw new Error(String(meldung));
  }
  return daten as Record<string, unknown>;
}

const STIL = `
  .cz-karte { max-width: 520px; margin: 48px auto; background: #ffffff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 32px; }
  .cz-karte h1 { font-size: 20px; margin: 0 0 12px; color: #111827; }
  .cz-karte p { color: #374151; line-height: 1.5; margin: 0 0 12px; }
  .cz-knoepfe { display: flex; gap: 12px; margin-top: 24px; }
  .cz-knopf { padding: 10px 18px; border-radius: 8px; border: 1px solid #d1d5db; background: #ffffff; color: #111827; cursor: pointer; font-size: 15px; }
  .cz-knopf.primaer { background: #3465de; border-color: #3465de; color: #ffffff; }
  .cz-knopf:disabled { opacity: 0.6; cursor: default; }
  .cz-fehler { color: #b91c1c; }
`;

export const ChatZustimmungPage: React.FC = () => {
  const [zustand, setZustand] = useState<Zustand>({ art: 'laden' });
  const [sendet, setSendet] = useState(false);
  const autorisierungsId = new URLSearchParams(window.location.search).get('authorization_id') || '';

  const pruefen = useCallback(async () => {
    if (!autorisierungsId) {
      setZustand({ art: 'fehler', text: 'Der Aufruf ist unvollständig. Bitte starten Sie die Verbindung in Claude erneut.' });
      return;
    }
    try {
      const client = zustimmungsVerbindung();
      const { data: { session } } = await client.auth.getSession();
      if (!session) {
        setZustand({ art: 'anmelden' });
        return;
      }
      const email = session.user.email || '';
      if (session.user.app_metadata?.rolle !== CHAT_ROLLE) {
        await client.auth.signOut({ scope: 'local' });
        setZustand({ art: 'kein_chat_konto', email });
        return;
      }
      const daten = await anmeldedienst(`/oauth/authorizations/${encodeURIComponent(autorisierungsId)}`, session);
      if (typeof daten.redirect_url === 'string') {
        // Zustimmung liegt bereits vor
        setZustand({ art: 'weiterleiten' });
        window.location.assign(daten.redirect_url);
        return;
      }
      setZustand({ art: 'zustimmen', anfrage: daten as unknown as Anfrage, email, session });
    } catch (err) {
      setZustand({ art: 'fehler', text: `Die Anfrage konnte nicht geladen werden: ${err instanceof Error ? err.message : String(err)}` });
    }
  }, [autorisierungsId]);

  useEffect(() => {
    pruefen();
  }, [pruefen]);

  const mitGoogleAnmelden = async () => {
    setSendet(true);
    const { error } = await zustimmungsVerbindung().auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/oauth/consent?authorization_id=${encodeURIComponent(autorisierungsId)}`,
        queryParams: { hd: 'xtax.de', prompt: 'select_account' }
      }
    });
    if (error) {
      setSendet(false);
      setZustand({ art: 'fehler', text: `Die Anmeldung mit Google konnte nicht gestartet werden: ${error.message}` });
    }
  };

  const entscheiden = async (aktion: 'approve' | 'deny') => {
    if (zustand.art !== 'zustimmen') return;
    setSendet(true);
    try {
      const daten = await anmeldedienst(`/oauth/authorizations/${encodeURIComponent(autorisierungsId)}/consent`, zustand.session, {
        method: 'POST',
        body: JSON.stringify({ action: aktion })
      });
      if (typeof daten.redirect_url !== 'string') throw new Error('Keine Rücksprungadresse erhalten');
      setZustand({ art: 'weiterleiten' });
      window.location.assign(daten.redirect_url);
    } catch (err) {
      setSendet(false);
      setZustand({ art: 'fehler', text: `Die Entscheidung konnte nicht übermittelt werden: ${err instanceof Error ? err.message : String(err)}` });
    }
  };

  const neuAnmelden = async () => {
    await zustimmungsVerbindung().auth.signOut({ scope: 'local' });
    setZustand({ art: 'anmelden' });
  };

  let inhalt: React.ReactNode;
  if (zustand.art === 'laden' || zustand.art === 'weiterleiten') {
    inhalt = <p>{zustand.art === 'laden' ? 'Einen Moment bitte ...' : 'Sie werden zu Claude zurückgeleitet ...'}</p>;
  } else if (zustand.art === 'anmelden') {
    inhalt = (
      <>
        <h1>Claude mit XTAX DATEV verbinden</h1>
        <p>Bitte melden Sie sich mit Ihrem XTAX-Google-Konto an. Danach entscheiden Sie, ob Claude in Ihrem Namen auf XTAX DATEV zugreifen darf.</p>
        <div className="cz-knoepfe">
          <button className="cz-knopf primaer" onClick={mitGoogleAnmelden} disabled={sendet}>Mit Google anmelden</button>
        </div>
      </>
    );
  } else if (zustand.art === 'kein_chat_konto') {
    inhalt = (
      <>
        <h1>Kein Zugang freigeschaltet</h1>
        <p>Für {zustand.email || 'dieses Konto'} ist der Zugang zu XTAX DATEV über Claude nicht freigeschaltet. Bitte wenden Sie sich an Tim Wolter.</p>
        <div className="cz-knoepfe">
          <button className="cz-knopf" onClick={neuAnmelden}>Mit einem anderen Konto anmelden</button>
        </div>
      </>
    );
  } else if (zustand.art === 'zustimmen') {
    const anwendung = zustand.anfrage.client?.name || 'Claude';
    inhalt = (
      <>
        <h1>{anwendung} möchte auf XTAX DATEV zugreifen</h1>
        <p>Angemeldet als <strong>{zustand.email}</strong>.</p>
        <p>{anwendung} kann damit in Ihrem Namen Daten aus dem DATEV-Spiegel und aus Smart Temping lesen, Schreibaufträge an DATEV Lohn und Gehalt anlegen und versehentlich angelegte Mitarbeiter in Smart Temping löschen. Jeder dieser Vorgänge wird Ihnen vorher zur Bestätigung angezeigt und trägt Ihre E-Mail-Adresse als Auslöser.</p>
        <div className="cz-knoepfe">
          <button className="cz-knopf primaer" onClick={() => entscheiden('approve')} disabled={sendet}>Zulassen</button>
          <button className="cz-knopf" onClick={() => entscheiden('deny')} disabled={sendet}>Ablehnen</button>
        </div>
      </>
    );
  } else {
    inhalt = (
      <>
        <h1>Verbindung nicht möglich</h1>
        <p className="cz-fehler">{zustand.text}</p>
      </>
    );
  }

  return (
    <>
      <style>{STIL}</style>
      <div className="cz-karte">{inhalt}</div>
    </>
  );
};
